/**
 * `useDetachArtifact` mutation hook.
 *
 * Wraps the raw `detachArtifact` service fn. Semantically this
 * removes the project↔artifact link only — the artifact persists in the
 * workspace. Cache invalidation targets list queries via
 * `PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId)` so the Library
 * grid refreshes across all filter/sort/page combinations without
 * unnecessarily clearing attachedIds or detail queries.
 *
 * Toast side effects are intentionally NOT handled here: the calling page
 * (`Library`) owns success/error toasts and modal close, mirroring the
 * `ProjectsTable` deletion flow.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { detachArtifact } from './project-artifacts.api';

export { detachArtifact };

interface UseDetachArtifactOptions {
  projectId: string;
}

export function useDetachArtifact(options: UseDetachArtifactOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation({
    mutationFn: (projectArtifactId: string) => detachArtifact(projectId, projectArtifactId),
    onSuccess: () => {
      // Invalidate only list queries (all filter/sort/page combos) without
      // clearing attachedIds or detail queries unnecessarily.
      void queryClient.invalidateQueries({
        queryKey: PROJECT_ARTIFACT_QUERY_KEYS.listsForProject(projectId),
      });
    },
  });
}
